import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";

import Dialog from "common/basicComponents/Dialog";
import Typography, {
  TypographyVariant,
  TypographyWeight,
} from "common/basicComponents/Typography";
import Button, { ButtonVariant } from "common/basicComponents/Button";
import { ButtonWidth } from "common/basicComponents/Button";
import Box from "common/basicComponents/Box";
import { ButtonType } from "common/basicComponents/Button";
import { useViewport } from "utils/ViewportContext";
import { ViewportType } from "utils/ViewportContext";
import FormInputField, {
  FormInputFieldType,
} from "common/formFields/FormInputField";
import ValidationSchema from "./DomainForm.schema";

const Buttons = styled.div`
  display: flex;
  flex-direction: ${({ viewport }) =>
    viewport > ViewportType.MOBILE ? "row" : "column-reverse"};
  width: 100%;
  gap: ${({ viewport }) =>
    viewport > ViewportType.MOBILE ? "2rem" : "1.25rem"};
  margin-top: 2.5rem;
`;

const EditDomainDialog = ({ open, data, onSave, onClose }) => {
  const { t } = useTranslation("domains");
  const { type: viewportType } = useViewport();

  const onSubmit = (values, { setSubmitting }) => {
    setSubmitting(true);
    onSave({
      ...data,
      domainId: data?.domainId,
      domainUrl: values.domainUrl,
    });
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box padding="0 0 2.5rem">
        <Typography
          variant={TypographyVariant.H4}
          weight={TypographyWeight.BOLD}
          text={data?.domainId ? t("editDomain") : t("addDomain")}
        />
      </Box>
      <Formik
        initialValues={{
          domainUrl: data?.domainUrl || "",
        }}
        onSubmit={onSubmit}
        enableReinitialize
        validationSchema={ValidationSchema}
      >
        {({ isSubmitting }) => (
          <Form>
            <Box padding="0.5rem 0 0" width="100%">
              <FormInputField
                type={FormInputFieldType.TEXT}
                name="domainUrl"
                id="domainUrl"
                label={t("domain")}
                disabled={isSubmitting}
              />
            </Box>
            <Buttons viewport={viewportType}>
              <Button
                variant={ButtonVariant.OUTLINED}
                text={t("common:button.cancel")}
                width={ButtonWidth.GROW}
                onClick={onClose}
                disabled={isSubmitting}
              />
              <Button
                variant={ButtonVariant.GREEN}
                text={t("saveDomain")}
                width={ButtonWidth.GROW}
                type={ButtonType.SUBMIT}
                isLoading={isSubmitting}
              />
            </Buttons>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

EditDomainDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
};

EditDomainDialog.defaultProps = {
  data: null,
};

export default EditDomainDialog;
